import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  User, Mail, Phone, Linkedin, Building2, Users, Briefcase, 
  ChevronRight, ChevronLeft, Check, Loader2,
  Globe, Shield, Award, Heart, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Thank You Page with auto-close countdown
const ThankYouPage = ({ pageSettings }) => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Try to close the window
          try {
            window.close();
          } catch (e) {
            // If window.close() doesn't work (wasn't opened by script), redirect to home or show message
            console.log('Cannot close window automatically');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Calculate progress percentage (decreasing from 100 to 0)
  const progressPercent = (countdown / 5) * 100;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-w-lg w-full"
      >
        <Card className="shadow-2xl border-0 overflow-hidden">
          {/* Countdown progress bar at top - decreasing */}
          <div className="h-1.5 bg-slate-100">
            <div 
              className="h-full bg-green-500 transition-all duration-1000 ease-linear"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Check className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2 sm:mb-3">Thank You!</h2>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
              We've received your information and will be in touch soon. We're excited about the possibility of working together!
            </p>
            <div className="flex flex-col gap-3 mb-6">
              {pageSettings?.website && (
                <a 
                  href={pageSettings.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Globe className="w-4 h-4" /> Visit our website
                </a>
              )}
              {pageSettings?.linkedin && (
                <a 
                  href={pageSettings.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Linkedin className="w-4 h-4" /> Connect on LinkedIn
                </a>
              )}
            </div>
            
            {/* Auto-close countdown */}
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
                <Clock className="w-4 h-4" />
                <span>This window will auto close in <span className="font-semibold text-slate-600">{countdown}</span> seconds</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

const INDUSTRIES = [
  'Technology / IT',
  'Healthcare',
  'Finance / Banking',
  'Manufacturing',
  'Retail / E-commerce',
  'Education',
  'Real Estate',
  'Hospitality',
  'Media / Entertainment',
  'Consulting',
  'Legal',
  'Non-profit',
  'Government',
  'Other'
];

const COMPANY_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1000+ employees'
];

const HRSignupPage = () => {
  const { slug } = useParams();
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    linkedin: '',
    company_name: '',
    company_size: '',
    industry: '',
    job_title: '',
    message: ''
  });

  const [errors, setErrors] = useState({});

  // Check if mandatory fields are filled
  const canSubmit = formData.first_name.trim() !== '' && 
                    formData.email.trim() !== '' && 
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  useEffect(() => {
    fetchPageSettings();
  }, [slug]);

  const fetchPageSettings = async () => {
    try {
      const res = await fetch(`${API}/api/hr-signup/settings/${slug || 'default'}`);
      if (res.ok) {
        const data = await res.json();
        setPageSettings(data);
      } else {
        // Use default settings
        setPageSettings({
          company_name: 'Daruwalla Consultancy Services',
          tagline: 'Connecting Great Companies with Exceptional Talent',
          description: 'Thank you for your interest. We\'d love to learn more about your hiring needs.',
          logo_url: '/images/daruwallas-logo.png',
          primary_color: '#475569',
          show_testimonials: true,
          contact_email: 'info@daruwallas.com',
          contact_phone: '+91 9167896969',
          website: 'https://daruwallas.com',
          linkedin: 'https://linkedin.com/company/daruwallas'
        });
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setPageSettings({
        company_name: 'Daruwalla Consultancy Services',
        tagline: 'Connecting Great Companies with Exceptional Talent',
        description: 'Thank you for your interest. We\'d love to learn more about your hiring needs.',
        logo_url: '/images/daruwallas-logo.png',
        primary_color: '#475569',
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      id: 'basic',
      title: 'Let\'s Start',
      subtitle: 'Tell us your name and email',
      fields: ['first_name', 'last_name', 'email'],
      required: ['first_name', 'email']
    },
    {
      id: 'contact',
      title: 'Contact Details',
      subtitle: 'How can we reach you?',
      fields: ['phone', 'linkedin'],
      required: []
    },
    {
      id: 'company',
      title: 'Your Company',
      subtitle: 'Tell us about your organization',
      fields: ['company_name', 'job_title', 'company_size', 'industry'],
      required: []
    },
    {
      id: 'final',
      title: 'Almost Done!',
      subtitle: 'Any specific requirements?',
      fields: ['message'],
      required: []
    }
  ];

  const validateStep = (stepIndex) => {
    const step = steps[stepIndex];
    const newErrors = {};
    
    step.required.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipAll = () => {
    if (!canSubmit) {
      toast.error('Please fill in Name and Email first');
      if (currentStep !== 0) setCurrentStep(0);
      return;
    }
    handleSubmit();
  };

  // Collect device and browser information
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let deviceType = 'Desktop';
    if (/mobile/i.test(ua)) deviceType = 'Mobile';
    else if (/tablet|ipad/i.test(ua)) deviceType = 'Tablet';
    
    let browser = 'Unknown';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    else if (ua.includes('Opera')) browser = 'Opera';
    
    let os = 'Unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    return {
      device_type: deviceType,
      browser: browser,
      os: os,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      user_agent: ua
    };
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setCurrentStep(0);
      toast.error('Please fill in Name and Email first');
      return;
    }

    setSubmitting(true);
    try {
      // Get device info
      const deviceInfo = getDeviceInfo();
      
      // Try to get location (will use IP-based fallback on server if not available)
      let locationData = {};
      try {
        if (navigator.geolocation) {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 3000 });
          }).catch(() => null);
          if (pos) {
            locationData = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude
            };
          }
        }
      } catch (e) {
        // Location not available, that's ok
      }
      
      const res = await fetch(`${API}/api/hr-signup/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          page_slug: slug || 'default',
          submitted_at: new Date().toISOString(),
          device_info: deviceInfo,
          location: locationData,
          referrer: document.referrer || ''
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to submit');
      }

      setSubmitted(true);
      toast.success('Thank you for your submission!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (submitted) {
    return <ThankYouPage pageSettings={pageSettings} />;
  }

  const primaryColor = pageSettings?.primary_color || '#475569';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header - Clean with just logo and company name */}
      <header className="py-4 sm:py-6 px-4 border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center">
          <div className="flex items-center gap-3">
            <img 
              src={pageSettings?.logo_url || "/images/daruwallas-logo.png"} 
              alt="Logo" 
              className="h-10 sm:h-12 w-auto object-contain"
              onError={(e) => { e.target.src = "/images/daruwallas-logo.png"; }}
            />
            <span className="font-semibold text-slate-800 text-base sm:text-lg hidden xs:block">
              {pageSettings?.company_name || 'Daruwalla Consultancy Services'}
            </span>
          </div>
        </div>
      </header>

      <main className="w-full max-w-7xl mx-auto px-4 py-6 sm:py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-start">
          {/* Left side - Info */}
          <div className="order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-3 sm:mb-4 leading-tight">
                {pageSettings?.tagline || 'Connecting Great Companies with Exceptional Talent'}
              </h1>
              <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8">
                {pageSettings?.description || "Thank you for your interest. We'd love to learn more about your hiring needs."}
              </p>

              {/* Trust indicators */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800 text-xs sm:text-sm">Trusted Partner</h3>
                    <p className="text-xs text-slate-500 hidden sm:block">100% confidential</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Award className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800 text-xs sm:text-sm">Quality First</h3>
                    <p className="text-xs text-slate-500 hidden sm:block">Exceptional candidates</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800 text-xs sm:text-sm">Team Scaling</h3>
                    <p className="text-xs text-slate-500 hidden sm:block">Resource augmentation</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-800 text-xs sm:text-sm">Relationship First</h3>
                    <p className="text-xs text-slate-500 hidden sm:block">Built on trust</p>
                  </div>
                </div>
              </div>

              {/* Quote - hidden on small mobile */}
              <blockquote className="hidden sm:block border-l-4 border-indigo-500 pl-4 py-2 italic text-slate-600 bg-white/50 rounded-r-lg text-sm sm:text-base">
                "Our philosophy is simple: keep things uncomplicated, deliver on our promises, and build relationships based on trust and mutual respect."
              </blockquote>
            </motion.div>
          </div>

          {/* Right side - Form */}
          <div className="order-1 lg:order-2 w-full">
            <Card className="shadow-xl sm:shadow-2xl border-0 overflow-hidden">
              {/* Progress bar */}
              <div className="h-1 bg-slate-100">
                <motion.div 
                  className="h-full bg-slate-600"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              <CardContent className="p-4 sm:p-6 md:p-8">
                {/* Step indicator */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Step {currentStep + 1} of {steps.length}</p>
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800">{steps[currentStep].title}</h2>
                    <p className="text-xs sm:text-sm text-slate-500">{steps[currentStep].subtitle}</p>
                  </div>
                  <div className="flex gap-1">
                    {steps.map((_, idx) => (
                      <div 
                        key={idx}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx <= currentStep ? 'bg-slate-600' : 'bg-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Form fields */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3 sm:space-y-4"
                  >
                    {currentStep === 0 && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">
                              First Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                value={formData.first_name}
                                onChange={(e) => handleChange('first_name', e.target.value)}
                                placeholder="John"
                                className={`pl-10 h-10 sm:h-11 ${errors.first_name ? 'border-red-500' : ''}`}
                                data-testid="hr-signup-first-name"
                              />
                            </div>
                            {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>}
                          </div>
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">Last Name</label>
                            <Input
                              value={formData.last_name}
                              onChange={(e) => handleChange('last_name', e.target.value)}
                              placeholder="Doe"
                              className="h-10 sm:h-11"
                              data-testid="hr-signup-last-name"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleChange('email', e.target.value)}
                              placeholder="john@company.com"
                              className={`pl-10 h-10 sm:h-11 ${errors.email ? 'border-red-500' : ''}`}
                              data-testid="hr-signup-email"
                            />
                          </div>
                          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                        </div>
                      </>
                    )}

                    {currentStep === 1 && (
                      <>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">Phone Number</label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              value={formData.phone}
                              onChange={(e) => handleChange('phone', e.target.value)}
                              placeholder="+91 9876543210"
                              className="pl-10 h-10 sm:h-11"
                              data-testid="hr-signup-phone"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">LinkedIn Profile</label>
                          <div className="relative">
                            <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              value={formData.linkedin}
                              onChange={(e) => handleChange('linkedin', e.target.value)}
                              placeholder="https://linkedin.com/in/yourprofile"
                              className="pl-10 h-10 sm:h-11"
                              data-testid="hr-signup-linkedin"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 2 && (
                      <>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">Company Name</label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              value={formData.company_name}
                              onChange={(e) => handleChange('company_name', e.target.value)}
                              placeholder="Your Company"
                              className="pl-10 h-10 sm:h-11"
                              data-testid="hr-signup-company"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">Job Title</label>
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              value={formData.job_title}
                              onChange={(e) => handleChange('job_title', e.target.value)}
                              placeholder="HR Manager"
                              className="pl-10 h-10 sm:h-11"
                              data-testid="hr-signup-job-title"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">Company Size</label>
                            <Select value={formData.company_size} onValueChange={(v) => handleChange('company_size', v)}>
                              <SelectTrigger className="h-10 sm:h-11" data-testid="hr-signup-company-size">
                                <SelectValue placeholder="Select size" />
                              </SelectTrigger>
                              <SelectContent>
                                {COMPANY_SIZES.map(size => (
                                  <SelectItem key={size} value={size}>{size}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">Industry</label>
                            <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                              <SelectTrigger className="h-10 sm:h-11" data-testid="hr-signup-industry">
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                              <SelectContent>
                                {INDUSTRIES.map(ind => (
                                  <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 3 && (
                      <div>
                        <label className="text-xs sm:text-sm font-medium text-slate-700 mb-1 block">
                          Any specific requirements or message? (Optional)
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder="Tell us about your hiring needs, timeline, or any questions you have..."
                          rows={4}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 resize-none"
                          data-testid="hr-signup-message"
                        />
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t">
                  <div className="order-2 sm:order-1">
                    {currentStep > 0 && (
                      <Button 
                        variant="ghost" 
                        onClick={handleBack} 
                        className="gap-1 w-full sm:w-auto"
                        data-testid="hr-signup-back-btn"
                      >
                        <ChevronLeft className="w-4 h-4" /> Back
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2">
                    {/* Skip All & Submit - always visible but disabled if mandatory fields not filled */}
                    <Button 
                      variant="outline" 
                      onClick={handleSkipAll}
                      disabled={submitting || !canSubmit}
                      className={`w-full sm:w-auto ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="hr-signup-skip-btn"
                    >
                      Skip Rest & Submit
                    </Button>
                    <Button 
                      onClick={handleNext}
                      disabled={submitting}
                      className="gap-1 w-full sm:w-auto bg-slate-800 hover:bg-slate-700"
                      data-testid="hr-signup-next-btn"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                      ) : currentStep === steps.length - 1 ? (
                        <><Check className="w-4 h-4" /> Submit</>
                      ) : (
                        <>Next <ChevronRight className="w-4 h-4" /></>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Skip hint */}
                {currentStep > 0 && currentStep < steps.length - 1 && (
                  <p className="text-center text-xs text-slate-400 mt-3 sm:mt-4">
                    All fields on this step are optional. Feel free to skip.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Privacy note */}
            <p className="text-center text-xs text-slate-400 mt-3 sm:mt-4 px-2">
              Your information is safe with us. We respect your privacy and will never share your data.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 border-t bg-white/50 mt-8 sm:mt-16">
        <div className="max-w-7xl mx-auto text-center text-xs sm:text-sm text-slate-500">
          <p>© {new Date().getFullYear()} {pageSettings?.company_name || 'Daruwalla Consultancy Services'}. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-3">
            {pageSettings?.website && (
              <a href={pageSettings.website} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600">
                <Globe className="w-4 h-4" />
              </a>
            )}
            {pageSettings?.linkedin && (
              <a href={pageSettings.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600">
                <Linkedin className="w-4 h-4" />
              </a>
            )}
            {pageSettings?.contact_email && (
              <a href={`mailto:${pageSettings.contact_email}`} className="hover:text-indigo-600">
                <Mail className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HRSignupPage;
